import {
  Box,
  BoxProps,
  Flex,
  Input,
  PseudoBox,
  useDisclosure,
} from "@chakra-ui/core"
import { isValidPhoneNumber } from "libphonenumber-js"
import React, { useRef, useState } from "react"
import NumberFormat from "react-number-format"
import { useDispatch, useSelector } from "react-redux"
import Body from "../typography/Body"
import { Button } from "./Buttons"
import { DesktopQuestionStepper } from "./QuestionStepper"
import { BaseDispatch, BaseRootState } from "../../redux/store"
import * as QuizTypes from "../../redux/types/QuizTypes"
import { bp } from "../../utils/MediaQueries"
import { unformatPhoneNumber } from "../../utils/text"

const QuestionText: React.FC = ({ children }) => (
  <Body
    size="lg"
    fontWeight="bold"
    color="#fff"
    mb={["1.25rem", null, null, null, "1.25rem"]}
    className="QuizQuestionText"
    textAlign="center"
    w="100%"
  >
    {children}
  </Body>
)

export interface QuestionProps {
  questionText: string
  currentQuestion: boolean
  options?: Array<QuizTypes.OptionProps>
  onAnswer: (answerId: string | Array<string>) => void
  answerId?: string
}

export const Question: React.FC<QuestionProps> = ({
  questionText,
  currentQuestion,
  options,
  onAnswer,
  answerId,
}) =>
  currentQuestion ? (
    // Question Option Buttons
    <Flex
      flexDir="row"
      mt={bp(0, 0)}
      w="100%"
      className="buttonFlex"
      justifyContent="center"
      alignItems="center"
      flexWrap={"wrap"}
      style={{ gap: "10px" }}
    >
      <QuestionText>{questionText}</QuestionText>
      {options?.map((opt) => (
        <Flex
          key={`${questionText}-question-opt-${opt.answerId}`}
          w="fit-content"
        >
          <Button
            className="QuizQuestionAnswerBtn"
            mb="0.2rem"
            w="fit-content"
            h="1.7rem"
            onClick={() => onAnswer(opt.answerId)}
            bg={answerId === opt.answerId ? "seafoam" : undefined}
            style={{ width: "fit-content !important" }}
            p="1.3rem 1rem"
          >
            {opt.answerText}
          </Button>
        </Flex>
      ))}
    </Flex>
  ) : (
    <></>
  )

export const NumberQuestion: React.FC<
  QuestionProps & { isEditing?: boolean }
> = ({ questionText, currentQuestion, options, onAnswer, isEditing }) => {
  const [answers, setAnswers] = React.useState<Array<string>>(
    options?.map((_) => "") || []
  )

  const haveAllQuestionsBeenAnswered = () =>
    answers.reduce((acc, cur) => {
      if (cur.length < 1) return false
      return acc
    }, true)
  return currentQuestion ? (
    <Flex
      flexDir="column"
      mt={bp(0, 0)}
      alignItems="center"
      className="questionContainer"
    >
      <QuestionText>{questionText}</QuestionText>
      <Flex w="100%" flexDir={"column"} alignItems="center">
        <Flex
          style={{ gap: "1rem" }}
          w="100%"
          alignItems="center"
          justifyContent="center"
        >
          {options?.map((opt, index) => (
            <Input
              className="QuizQuestionInput"
              key={`question-opt${opt.answerId}`}
              placeholder={opt.answerText}
              type="number"
              h="3.5rem"
              width="3.5rem"
              p="0"
              color="dawn"
              fontSize="body15"
              lineHeight="1.25rem"
              letterSpacing="-0.15px"
              textAlign="center"
              // mr={["0.75rem", null, null, null, "1rem"]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setAnswers(
                  answers.map((a, i) => {
                    if (i === index) {
                      const value = parseInt(event.target.value)
                      // @ts-ignore
                      if (value !== NaN) {
                        return `${value}`
                      }
                    }
                    return a
                  })
                )
              }
            />
          ))}
        </Flex>
        <Button
          className="QuizQuestionButton extraControls"
          isDisabled={!haveAllQuestionsBeenAnswered()}
          theme={!haveAllQuestionsBeenAnswered() ? "secondary" : "primary"}
          ml={"0"}
          mt={bp("1.2rem", "2rem")}
          mb={bp("0.5rem", 0)}
          w="100%"
          onClick={() => onAnswer(answers)}
          h="2.7rem"
          style={{ width: "fit-content" }}
        >
          {isEditing !== undefined ? "save" : "next"}
        </Button>
      </Flex>
    </Flex>
  ) : (
    <></>
  )
}

export const GenderQuestion = (props: QuestionProps) => (
  <Question
    {...props}
    options={[
      {
        answerText: "Womens",
        answerId: "Womens",
        type: "option",
        riderType: [],
        filterBikes: true,
      },
      {
        answerText: "Mens",
        answerId: "Mens",
        type: "option",
        riderType: [],
        filterBikes: true,
      },
      {
        answerText: "Either",
        answerId: "Either",
        type: "option",
        riderType: [],
        filterBikes: true,
      },
    ]}
  />
)

export const EmailQuestion: React.FC<
  Omit<QuestionProps, "options"> & {
    isEditing?: boolean
    defaultValue?: string | null
  }
> = ({ currentQuestion, questionText, onAnswer, isEditing, defaultValue }) => {
  const [value, setValue] = useState(defaultValue || "")
  const inputRef = useRef<HTMLInputElement | null>(null)
  const isValid = inputRef.current
    ? !!inputRef.current.validity.valid
    : !!defaultValue
  return currentQuestion ? (
    <Flex flexDir="column" mt={bp(0, 0)}>
      <QuestionText>{questionText}</QuestionText>
      <Flex flexDir="column">
        <Flex>
          <Input
            ref={inputRef}
            placeholder="Email"
            type="email"
            value={value}
            h="3.5rem"
            color="night"
            fontSize="body15"
            lineHeight="1.25rem"
            letterSpacing="-0.15px"
            isRequired
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setValue(event.target.value)
            }
          />
        </Flex>
        <Button
          className="QuizButtonWhite"
          isDisabled={!isValid}
          theme={!isValid ? "secondary" : "primary"}
          mt="1rem"
          w="100%"
          onClick={() => onAnswer(value)}
          h="3.5rem"
          style={{ width: "100%" }}
        >
          {isEditing !== undefined ? "Save" : "Next"}
        </Button>
      </Flex>
    </Flex>
  ) : (
    <></>
  )
}

export const PhoneQuestion: React.FC<
  Omit<QuestionProps, "options"> & { isEditing?: boolean }
> = ({ currentQuestion, questionText, onAnswer, isEditing }) => {
  const [value, setValue] = useState("")
  const unformatted = unformatPhoneNumber(value)
  const isValid = isValidPhoneNumber(`+1${unformatted}`, "US")

  return currentQuestion ? (
    <Flex flexDir="column" mt={bp(0, 0)}>
      <QuestionText>{questionText}</QuestionText>
      <Flex flexDir="column">
        <Flex>
          <Input
            as={NumberFormat}
            // @ts-ignore
            name={name}
            type="tel"
            h="3.5rem"
            // @ts-ignore
            format="(###) ###-####"
            allowEmptyFormatting
            removeFormatting={unformatPhoneNumber}
            mask="_"
            value={value}
            customInput={Input}
            onValueChange={(values) => {
              setValue(values.value)
            }}
          />
        </Flex>
        <Button
          isDisabled={!isValid}
          className="QuizButtonWhite"
          theme={!isValid ? "secondary" : "primary"}
          mt="1rem"
          w="100%"
          onClick={() => onAnswer(value)}
          h="3.5rem"
          style={{ width: "100%" }}
        >
          {isEditing !== undefined ? "Save" : "Next"}
        </Button>
        {!isValid && unformatted.length === 10 && (
          <Body color="sunrise" mt="0.5rem">
            This phone number is not valid in the US
          </Body>
        )}
      </Flex>
    </Flex>
  ) : (
    <></>
  )
}

type QuizQuestionsProps = {
  defaultEmail?: string
  className?: string
  onExit?: VoidFunction
  style: any
}

const QuizQuestions: React.FC<QuizQuestionsProps> = ({
  defaultEmail = null,
  className,
  onExit,
  style,
}) => {
  const dispatch = useDispatch<BaseDispatch>()
  const { questions, quizProgressState, currentQuestionIndex, answers } =
    useSelector(({ quiz }: BaseRootState) => quiz)
  const confirmExit = useDisclosure()

  const onStepNext = () => {
    dispatch.quiz.nextQuestion()
  }
  const onStepPrev = () => {
    dispatch.quiz.prevQuestion()
  }

  const interactiveProps: BoxProps = {
    opacity: confirmExit.isOpen ? 0.2 : 1,
    transition: "opacity 300ms ease",
    pointerEvents: confirmExit.isOpen ? "none" : "auto",
  }

  return (
    <Flex
      className={className}
      position="relative"
      flexDir="column"
      height="100%"
      pt={"3rem"}
      pb={"0.5rem"}
      justifyContent={"space-between"}
      alignItems="center"
      alignContent="center"
      mx={bp(0, 0)}
      style={style}
    >
      <PseudoBox w="100%" {...interactiveProps}>
        {questions.map((question, index) => {
          const questionProps = {
            ...question,
            currentQuestion: currentQuestionIndex === index,
            onAnswer: (answerId: string | Array<string>) => {
              dispatch.quiz.answerQuestion({
                questionId: question.questionId,
                answerId,
              })
              dispatch.quiz.nextQuestion()
              question.onAnswer?.(answerId)
            },
          }
          if (question.questionId === "email") {
            return (
              <EmailQuestion
                key={`question-${question.questionId}`}
                defaultValue={defaultEmail}
                {...questionProps}
              />
            )
          }
          if (question.questionId === "phone") {
            return (
              <PhoneQuestion
                key={`question-${question.questionId}`}
                {...questionProps}
              />
            )
          }
          if (
            question.questionId === "height" ||
            question.questionId === "weight"
          ) {
            return (
              <NumberQuestion
                key={`question-${question.questionId}`}
                {...questionProps}
              />
            )
          }
          if (question.questionId === "gender") {
            return (
              <GenderQuestion
                key={`question-${question.questionId}`}
                {...questionProps}
              />
            )
          }
          return (
            <Question
              key={`question-${question.questionId}`}
              {...questionProps}
            />
          )
        })}
      </PseudoBox>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignContent="center"
        alignItems="center"
        w="100%"
        pt={bp("25px", 0)}
      >
        <DesktopQuestionStepper
          className="BodyQuizQuestionPagination"
          color="#fff"
          currentQuestionNumber={currentQuestionIndex + 1}
          totalQuestionNumber={questions.length}
          canGoToNextQuestion={
            answers[questions[currentQuestionIndex]?.questionId] !== undefined
          }
          quizProgressState={quizProgressState}
          onNext={onStepNext}
          onPrev={onStepPrev}
          {...interactiveProps}
        />

        <Box display={bp("block", "block")}>
          {confirmExit.isOpen && (
            <>
              <Body mb="1rem" color="#fff">
                Are you sure?
              </Body>
              <Flex flexDir={bp("row", "row")}>
                <Button
                  className="QuizOutlineBtn"
                  flexGrow={1}
                  onClick={() => {
                    confirmExit.onClose()
                    onExit?.()
                  }}
                  w="fit-content"
                  mr="0.5rem"
                  style={{
                    width: "fit-content",
                    padding: "1rem",
                    height: "1.7rem",
                  }}
                >
                  Yes
                </Button>
                <Button
                  flexGrow={1}
                  theme="secondary"
                  onClick={confirmExit.onClose}
                  mt={bp(0, 0)}
                  w="fit-content"
                  border="1px solid #fff"
                  style={{
                    width: "fit-content",
                    padding: "1rem",
                    height: "1.7rem",
                  }}
                >
                  No
                </Button>
              </Flex>
            </>
          )}
          {!confirmExit.isOpen && (
            <Button
              className="BodyFitQuizExitBtn"
              w="100%"
              h="2rem"
              pl="1.5rem"
              pr="1.5rem"
              theme="tertiary"
              color="#fff"
              onClick={confirmExit.onOpen}
              style={{ width: "fit-content" }}
            >
              Exit
            </Button>
          )}
        </Box>
      </Flex>
    </Flex>
  )
}

export default QuizQuestions
